import React, { useEffect, useState } from 'react';
import {
  Box, Drawer, Stack, Typography, Chip, Divider, Avatar,
  Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import { LoadingOverlay, EmptyState, Code, StatusChip, GlassIconButton } from 'dashboard/components/common';

const ACTION_LABEL = {
  'user.enable': 'Enabled user',
  'user.disable': 'Disabled user',
  'user.password_reset': 'Sent password reset',
  'brand.enable': 'Enabled brand',
  'brand.disable': 'Disabled brand',
  'sub.cancel': 'Cancelled subscription',
  'sub.cancel.immediate': 'Cancelled (immediate)',
  'sub.resume': 'Resumed subscription',
  'sub.refund.full': 'Refunded (full)',
  'sub.refund.partial': 'Refunded (partial)',
  'domain.add': 'Added domain',
  'domain.delete': 'Deleted domain',
  'domain.verify.success': 'Domain verified',
  'domain.verify.fail': 'Domain verify failed',
};

const fmtTime = (ts) => (ts ? new Date(ts).toLocaleString() : '—');

const targetLabel = (t) => {
  if (t === 'webapp') return 'Storefront';
  if (t === 'dashboard') return 'Dashboard';
  if (t === 'page') return 'Page';
  return 'Coming soon';
};

/**
 * Right-side drawer with everything we know about one brand: primary
 * domain + verification status, subdomain routing rules, owner,
 * team members, and recent admin activity targeting this brand.
 *
 * Lazy fetch — only hits the backend when the drawer actually opens.
 */
export default function BrandDetailsDrawer({ brandId, onClose }) {
  const open = !!brandId;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!brandId) { setData(null); return undefined; }
    let cancelled = false;
    setLoading(true);
    API.adminBrandDetails({ _id: brandId })
      .then((res) => { if (!cancelled) setData(res); })
      .catch((err) => { if (!cancelled) toast.error(err?.message || 'Failed to load brand details'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [brandId]);

  const b = data?.brand;

  return (
    <Drawer anchor='right' open={open} onClose={onClose}>
      <Box sx={{ width: { xs: '100vw', sm: 520 }, p: '20px' }}>
        <Stack direction='row' justifyContent='space-between' alignItems='center' mb='14px'>
          <Typography variant='h6' fontWeight='700'>Brand details</Typography>
          <GlassIconButton size='small' label='Close' onClick={onClose}>
            <CloseIcon fontSize='small' />
          </GlassIconButton>
        </Stack>

        <LoadingOverlay loading={loading && !data} minHeight={240}>
          {b && (
            <>
              <Stack direction='row' spacing='12px' alignItems='center' mb='12px'>
                <Avatar src={b.logo_upload_path || undefined} sx={{ width: 48, height: 48 }}>
                  {(b.name || '?').slice(0, 1)}
                </Avatar>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Stack direction='row' spacing='6px' alignItems='center'>
                    <Typography fontWeight='700' noWrap>{b.name}</Typography>
                    {b.is_active === false && <Chip size='small' color='error' label='Disabled' />}
                  </Stack>
                  <Typography variant='caption' color='text.secondary'>
                    {b.slug && <Code>{b.slug}</Code>}
                  </Typography>
                </Box>
              </Stack>

              <Typography variant='caption' color='text.secondary' display='block' mb='14px'>
                Created {fmtTime(b.createdAt)} · ID <Code>{String(b._id)}</Code>
              </Typography>

              <Divider sx={{ my: '14px' }} />

              <Typography variant='caption' color='text.secondary' textTransform='uppercase' fontWeight='700' display='block' mb='8px'>
                Owner
              </Typography>
              {data.owner ? (
                <Paper elevation={0} sx={{ p: '10px', border: '1px solid', borderColor: 'divider', borderRadius: '8px', mb: '10px' }}>
                  <Typography variant='body2' fontWeight='600'>{data.owner.email}</Typography>
                  {data.owner.username && (
                    <Typography variant='caption' color='text.secondary'>{data.owner.username}</Typography>
                  )}
                </Paper>
              ) : (
                <EmptyState>No owner attached (orphaned brand).</EmptyState>
              )}

              {data.members?.length > 0 && (
                <>
                  <Typography variant='caption' color='text.secondary' textTransform='uppercase' fontWeight='700' display='block' mt='14px' mb='8px'>
                    Team ({data.members.length})
                  </Typography>
                  <Stack spacing='4px' mb='6px'>
                    {data.members.map((m) => (
                      <Stack key={m.user_id} direction='row' justifyContent='space-between' alignItems='center'>
                        <Typography variant='body2'>{m.email || String(m.user_id)}</Typography>
                        <Stack direction='row' spacing='4px'>
                          {(m.roles || []).map((r) => (
                            <Chip key={r} size='small' variant='outlined' label={r} />
                          ))}
                        </Stack>
                      </Stack>
                    ))}
                  </Stack>
                </>
              )}

              <Divider sx={{ my: '14px' }} />

              <Typography variant='caption' color='text.secondary' textTransform='uppercase' fontWeight='700' display='block' mb='8px'>
                Primary domain
              </Typography>
              {data.primary_domain ? (
                <Paper elevation={0} sx={{ p: '10px', border: '1px solid', borderColor: 'divider', borderRadius: '8px', mb: '10px' }}>
                  <Stack direction='row' justifyContent='space-between' alignItems='center'>
                    <Code>{data.primary_domain.domain}</Code>
                    <StatusChip status={data.primary_domain.status} />
                  </Stack>
                  {data.primary_domain.verified_at && (
                    <Typography variant='caption' color='text.secondary' display='block' mt='4px'>
                      Verified {fmtTime(data.primary_domain.verified_at)}
                    </Typography>
                  )}
                </Paper>
              ) : (
                <EmptyState>No primary domain configured.</EmptyState>
              )}

              <Typography variant='caption' color='text.secondary' textTransform='uppercase' fontWeight='700' display='block' mt='14px' mb='8px'>
                Subdomain rules ({data.subdomains?.length || 0})
              </Typography>
              {data.subdomains?.length ? (
                <Stack direction='row' spacing='6px' flexWrap='wrap' useFlexGap>
                  {data.subdomains.map((s) => (
                    <Chip
                      key={s._id}
                      size='small'
                      variant={s.enabled ? 'filled' : 'outlined'}
                      label={`${s.subdomain || '(apex)'} → ${targetLabel(s.target_type)}`}
                      color={s.target_type === 'coming_soon' ? 'default' : 'primary'}
                    />
                  ))}
                </Stack>
              ) : (
                <Typography variant='body2' color='text.secondary'>
                  No subdomain rules — every unmapped subdomain falls back to Coming Soon.
                </Typography>
              )}

              <Divider sx={{ my: '14px' }} />

              <Typography variant='caption' color='text.secondary' textTransform='uppercase' fontWeight='700' display='block' mb='8px'>
                Recent activity ({data.recent_audit?.length || 0})
              </Typography>
              {data.recent_audit?.length ? (
                <Stack spacing='6px'>
                  {data.recent_audit.map((e) => (
                    <Paper key={e._id} elevation={0} sx={{ p: '10px', border: '1px solid', borderColor: 'divider', borderRadius: '8px' }}>
                      <Stack direction='row' justifyContent='space-between' alignItems='center' spacing='8px'>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant='body2' noWrap>
                            <strong>{ACTION_LABEL[e.action] || e.action}</strong>
                          </Typography>
                          <Typography variant='caption' color='text.secondary'>
                            by {e.actor_email || 'admin'}
                          </Typography>
                        </Box>
                        <Typography variant='caption' color='text.secondary' sx={{ whiteSpace: 'nowrap' }}>
                          {fmtTime(e.createdAt)}
                        </Typography>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <EmptyState>No recorded admin activity for this brand.</EmptyState>
              )}
            </>
          )}
        </LoadingOverlay>
      </Box>
    </Drawer>
  );
}
